import AtriumApi from "api";

const heartbeatCampaignService = {
  // Get all campaigns for organization
  getCampaigns: async (params = {}) => {
    try {
      const response = await AtriumApi.get("/v2/heartbeat/campaigns", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching heartbeat campaigns:", error);
      throw error;
    }
  },

  // Get campaign by ID
  getCampaignById: async (id) => {
    try {
      const response = await AtriumApi.get(`/v2/heartbeat/campaigns/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching heartbeat campaign:", error);
      throw error;
    }
  },

  // Create new campaign
  createCampaign: async (campaignData) => {
    try {
      const response = await AtriumApi.post("/v2/heartbeat/campaigns", campaignData);
      return response.data;
    } catch (error) {
      console.error("Error creating heartbeat campaign:", error);
      throw error;
    }
  },

  // Update campaign
  updateCampaign: async (id, campaignData) => {
    try {
      const response = await AtriumApi.put(`/v2/heartbeat/campaigns/${id}`, campaignData);
      return response.data;
    } catch (error) {
      console.error("Error updating heartbeat campaign:", error);
      throw error;
    }
  },

  // Delete campaign
  deleteCampaign: async (id) => {
    try {
      const response = await AtriumApi.delete(`/v2/heartbeat/campaigns/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting heartbeat campaign:", error);
      throw error;
    }
  },

  // Duplicate campaign
  duplicateCampaign: async (id) => {
    try {
      const response = await AtriumApi.post(`/v2/heartbeat/campaigns/${id}/duplicate`);
      return response.data;
    } catch (error) {
      console.error("Error duplicating heartbeat campaign:", error);
      throw error;
    }
  },

  // Get campaign statistics
  getCampaignStats: async (id) => {
    try {
      const response = await AtriumApi.get(`/v2/heartbeat/campaigns/${id}/stats`);
      return response.data;
    } catch (error) {
      console.error("Error fetching campaign stats:", error);
      throw error;
    }
  },

  // Get default questions template
  getDefaultQuestions: async () => {
    try {
      const response = await AtriumApi.get("/v2/heartbeat/campaigns/default-questions");
      return response.data;
    } catch (error) {
      console.error("Error fetching default questions:", error);
      throw error;
    }
  },

  // Get campaign analytics (enhanced stats with SendGrid metrics)
  getCampaignAnalytics: async (id) => {
    try {
      const response = await AtriumApi.get(`/v2/heartbeat/campaigns/${id}/analytics`);
      return response.data;
    } catch (error) {
      console.error("Error fetching campaign analytics:", error);
      throw error;
    }
  },

  // Get question-level analytics (mean, distribution for numeric; count for text)
  getQuestionAnalytics: async (id) => {
    try {
      const response = await AtriumApi.get(`/v2/heartbeat/campaigns/${id}/question-analytics`);
      return response.data;
    } catch (error) {
      console.error("Error fetching question analytics:", error);
      throw error;
    }
  },

  // Generate AI summary for a text question
  generateQuestionSummary: async (campaignId, questionId) => {
    try {
      const response = await AtriumApi.post(
        `/v2/heartbeat/campaigns/${campaignId}/questions/${questionId}/summarize`
      );
      return response.data;
    } catch (error) {
      console.error("Error generating question summary:", error);
      throw error;
    }
  },

  // Get potential recipients for a campaign
  getPotentialRecipients: async (params = {}) => {
    try {
      const response = await AtriumApi.get("/v2/heartbeat/campaigns/potential-recipients", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching potential recipients:", error);
      throw error;
    }
  },

  // Send campaign to all eligible recipients immediately
  sendNow: async (id, filterCriteria = null, deliveryMethod = null) => {
    try {
      const response = await AtriumApi.post(`/v2/heartbeat/campaigns/${id}/send-now`, { filterCriteria, deliveryMethod });
      return response.data;
    } catch (error) {
      console.error("Error sending campaign:", error);
      throw error;
    }
  },

  // Generate preview link for campaign
  generatePreview: async (id) => {
    try {
      const response = await AtriumApi.post(`/v2/heartbeat/campaigns/${id}/preview`);
      return response.data;
    } catch (error) {
      console.error("Error generating preview:", error);
      throw error;
    }
  },

  // ==========================================
  // Spiritual Campaign Methods
  // ==========================================

  // Get default slider questions for spiritual campaigns
  getDefaultSliderQuestions: async () => {
    try {
      const response = await AtriumApi.get("/v2/heartbeat/campaigns/default-slider-questions");
      return response.data;
    } catch (error) {
      console.error("Error fetching default slider questions:", error);
      throw error;
    }
  },

  // Generate AI questions (can be used with or without campaign ID)
  generateAiQuestions: async (id, options = {}) => {
    try {
      // Use generic endpoint (no ID required) - works for both new and existing campaigns
      const response = await AtriumApi.post(`/v2/heartbeat/campaigns/generate-questions`, {
        campaignType: options.campaignType || "standard",
        focusAreas: options.focusAreas || ["faith_journey", "community", "life_challenges"],
        surveyType: options.surveyType || "spiritual",
        surveyTopic: options.surveyTopic || "",
        surveyGoals: options.surveyGoals || [],
      });
      return response.data;
    } catch (error) {
      console.error("Error generating AI questions:", error);
      throw error;
    }
  },

  // Generate QR code for a campaign
  generateQrCode: async (id, regenerate = false) => {
    try {
      const response = await AtriumApi.post(`/v2/heartbeat/campaigns/${id}/generate-qr`, {
        regenerate,
      });
      return response.data;
    } catch (error) {
      console.error("Error generating QR code:", error);
      throw error;
    }
  },

  // Get QR code statistics for a campaign
  getQrStats: async (id, params = {}) => {
    try {
      const response = await AtriumApi.get(`/v2/heartbeat/campaigns/${id}/qr-stats`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching QR stats:", error);
      throw error;
    }
  },

  // Generate AI campaign summary
  generateCampaignSummary: async (id) => {
    try {
      const response = await AtriumApi.post(`/v2/heartbeat/campaigns/${id}/generate-summary`);
      return response.data;
    } catch (error) {
      console.error("Error generating campaign summary:", error);
      throw error;
    }
  },

  // ==========================================
  // Question Template Methods
  // ==========================================

  // Get all question templates
  getQuestionTemplates: async () => {
    try {
      const response = await AtriumApi.get("/v2/heartbeat/campaigns/templates");
      return response.data;
    } catch (error) {
      console.error("Error fetching question templates:", error);
      throw error;
    }
  },

  // Save a question template
  saveQuestionTemplate: async (templateData) => {
    try {
      const response = await AtriumApi.post("/v2/heartbeat/campaigns/templates", templateData);
      return response.data;
    } catch (error) {
      console.error("Error saving question template:", error);
      throw error;
    }
  },

  // Delete a question template
  deleteQuestionTemplate: async (id) => {
    try {
      const response = await AtriumApi.delete(`/v2/heartbeat/campaigns/templates/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting question template:", error);
      throw error;
    }
  },

  // Get text responses for a specific question
  getQuestionResponses: async (campaignId, questionId, params = {}) => {
    try {
      const response = await AtriumApi.get(
        `/v2/heartbeat/campaigns/${campaignId}/questions/${questionId}/responses`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching question responses:", error);
      throw error;
    }
  },
};

export default heartbeatCampaignService;
